import { Alert, Badge, Button, Group, Image, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import { IconBook2, IconSparkles } from '@tabler/icons-react';
import type { GameParticipantView, GameSessionView } from '@creativitas/shared';
import { MASTER_SEAL_DECLARATION, MASTER_SEAL_TITLE, isPlayerKind } from '@creativitas/shared';

/**
 * Демо: кто закрывает сказку и какое пояснение к ней показать. В одиночной
 * партии ведущего нет — сказку закрывает сам игрок кнопкой «Продолжить», а
 * пояснение для ведущего («попросите поддержку группы») ему не подходит.
 */
export type GameIntroDemo = {
  canClose: boolean;
  closeLabel: string;
  guidance: string | null;
};

export type GameIntroActions = {
  openStory?: (sectorId: string) => void;
  closeStory?: () => void;
  startTalent?: (participantId: string) => void;
};

/**
 * The opening of a party and the sector stories that follow (GAME-31, GAME-32).
 *
 * Both are the facilitator's to time. Дарья was explicit that a story should
 * not pop up by itself and should not be pulled by each player separately —
 * «чтобы каждый сказку не тянул, чтобы она просто один раз вот так появлялась
 * на экране». So the button belongs to the facilitator, the reading appears in
 * every window at once, and the turn waits until it is closed.
 *
 * Everything shown here is decided on the server: this component renders
 * `pendingStory` and `awaitingStartTalent` and never works out for itself
 * whether a story is due.
 */
export function GameIntro({
  session,
  viewerParticipantId,
  facilitator,
  busy,
  actions,
  demo = null,
}: {
  session: GameSessionView;
  viewerParticipantId?: string;
  facilitator: boolean;
  busy: boolean;
  actions: GameIntroActions;
  /** В демо панель хода ведёт шаги сама — здесь остаётся только окно сказки. */
  demo?: GameIntroDemo | null;
}) {
  const story = session.pendingStory;
  const canClose = demo ? demo.canClose : facilitator;
  const guidance = demo ? demo.guidance : story?.guidance ?? null;
  const players = session.participants.filter((participant) => isPlayerKind(participant.kind));
  const awaiting = session.awaitingStartTalent;
  const inIntro = session.phase === 'intro';

  if (!story && awaiting.length === 0) return null;

  return (
    <>
      {/*
        The open story is a modal because it is the only thing happening: the
        room reads it together and the turn cannot move until it is closed.
      */}
      <Modal
        opened={story?.stage === 'open'}
        onClose={() => canClose && actions.closeStory?.()}
        title={story ? `Сказка сектора «${story.title}»` : ''}
        size="lg"
        centered
        withCloseButton={canClose}
        closeOnClickOutside={false}
        closeOnEscape={false}
        data-testid="sector-story-modal"
      >
        <Stack gap="md">
          {/*
            Всё окно выровнено по центру, как окно печати мастера: сказку
            читают вслух, как стихи, и её короткие строки слева от пустого
            поля выглядели обрывком. Кнопка — по той же оси, под текстом.

            Сначала текст, потом картинка. Сказка — то, что ведущий читает
            вслух; сцена сектора рядом с ней иллюстрация, а не содержание. На
            показе было наоборот, и первое, что сказала Дарья, открыв окно:
            «правда, это не сказка, да?».

            Переносы строк сохраняются: разбивка задаёт дыхание чтения, и
            склеенный в абзац текст читается вслух совсем иначе.
          */}
          {story?.text ? (
            <Text
              ta="center"
              ff="var(--font-display)"
              fz={{ base: 'lg', sm: 'xl' }}
              style={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}
              data-testid="sector-story-text"
            >
              {story.text}
            </Text>
          ) : null}
          {story?.image ? (
            <Image
              src={story.image}
              alt={`Сцена сектора «${story.title}»`}
              radius="md"
              fit="contain"
              mah={280}
            />
          ) : null}
          {/* Подсказка ведущему — не часть сказки, поэтому тише и мельче. */}
          {guidance ? (
            <Text ta="center" size="sm" c="var(--text-muted)" data-testid="sector-story-guidance">{guidance}</Text>
          ) : null}
          {canClose ? (
            <Group justify="center">
              <Button loading={busy} onClick={() => actions.closeStory?.()} data-testid="close-story">
                {demo?.closeLabel ?? 'Сказка прочитана'}
              </Button>
            </Group>
          ) : (
            <Text ta="center" size="sm" c="var(--text-muted)">
              Ведущий закроет сказку, когда группа будет готова продолжить.
            </Text>
          )}
        </Stack>
      </Modal>

      {demo ? null : <Stack gap="md">
        {story?.stage === 'awaiting_open' ? (
          <Alert
            color="gold"
            icon={<IconBook2 size={18} />}
            title={`Сектор «${story.title}» ещё не представлен`}
            data-testid="story-pending"
          >
            <Stack gap="sm">
              <Text size="sm">
                {inIntro
                  ? 'Партия начинается со сказки Маяка: её слышат все участники, и только после неё каждый вытягивает талант от рождения.'
                  : 'Сказка сектора звучит один раз за партию — для всей комнаты. Ход продолжится после неё.'}
              </Text>
              {facilitator ? (
                <Group>
                  <Button
                    leftSection={<IconBook2 size={16} />}
                    loading={busy}
                    onClick={() => actions.openStory?.(story.sectorId)}
                  >
                    Открыть сказку сектора «{story.title}»
                  </Button>
                </Group>
              ) : (
                <Text size="sm" c="var(--text-muted)">Ждём ведущего.</Text>
              )}
            </Stack>
          </Alert>
        ) : null}

        {inIntro && !story && awaiting.length > 0 ? (
          <Paper withBorder p="md" data-testid="start-talents">
            <Stack gap="sm">
              <Group gap="xs">
                <IconSparkles size={18} color="var(--accent)" />
                <Title order={4}>Талант от рождения</Title>
                <Badge variant="light" color="gold">{awaiting.length} осталось</Badge>
              </Group>
              <Text size="sm" c="var(--text-muted)">
                Каждый участник вытягивает одну карту из колоды «Таланты». Кубик появится,
                когда карты будут у всех.
              </Text>
              <Group gap="xs" wrap="wrap">
                {awaiting.map((participantId) => (
                  <StartTalentButton
                    key={participantId}
                    player={players.find((item) => item.id === participantId)}
                    participantId={participantId}
                    /*
                      The facilitator can draw for somebody who dropped out —
                      a live room must not stall on one lost connection.
                    */
                    enabled={facilitator || participantId === viewerParticipantId}
                    busy={busy}
                    onDraw={() => actions.startTalent?.(participantId)}
                  />
                ))}
              </Group>
            </Stack>
          </Paper>
        ) : null}
      </Stack>}
    </>
  );
}

function StartTalentButton({
  player,
  participantId,
  enabled,
  busy,
  onDraw,
}: {
  player?: GameParticipantView;
  participantId: string;
  enabled: boolean;
  busy: boolean;
  onDraw: () => void;
}) {
  const name = player?.displayName ?? 'Участник';
  return (
    <Button
      variant={enabled ? 'filled' : 'default'}
      disabled={!enabled || busy}
      onClick={onDraw}
      data-testid={`start-talent-${participantId}`}
      leftSection={<IconSparkles size={15} />}
    >
      {name}
    </Button>
  );
}

/**
 * «Печать Мастера» — the words a player says on reaching the Lighthouse
 * (GAME-38).
 *
 * The text comes from the shared game data, not from this file: Дарья read it
 * out of the end of the printed instruction, and it is the game's line rather
 * than the interface's. The modal is dismissible and the same words stay on the
 * player's card, so anybody can read them again after the party.
 */
export function MasterSeal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={MASTER_SEAL_TITLE}
      size="lg"
      centered
      data-testid="master-seal"
    >
      <Stack gap="lg" align="center" py="md">
        <Text
          ta="center"
          fz={{ base: 'lg', sm: 'xl' }}
          ff="var(--font-display)"
          style={{ lineHeight: 1.5, maxWidth: '34rem' }}
        >
          {MASTER_SEAL_DECLARATION}
        </Text>
        <Button onClick={onClose}>Я произнёс(ла) эти слова</Button>
      </Stack>
    </Modal>
  );
}
